import React, { useState, useEffect, useRef } from 'react';
import { Copy, Repeat } from 'lucide-react';
import toast from 'react-hot-toast';

// ChatMessage Component
interface ChatMessageProps {
  isUser: boolean;
  content: string;
  userName?: string;
  image?: string;
  index: number;
  handleRegenerate: (content: any) => void;
  isLatest?: boolean; // New prop to indicate if this is the latest message
}

export const ChatMessage: React.FC<ChatMessageProps> = ({
  isUser,
  content,
  userName,
  image,
  index,
  handleRegenerate,
  isLatest = false, // Default to false
}) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const typingSpeed = 10; // milliseconds per character
  const contentRef = useRef(content);
  const messageEndRef = useRef<HTMLDivElement>(null);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    // Skip animation for user messages or if not the latest message
    if (isUser || !isLatest) {
      setDisplayedText(content);
      return;
    }

    // Reset if content changes
    if (content !== contentRef.current) {
      setDisplayedText('');
      contentRef.current = content;
    }

    // If we haven't displayed the full text yet, start/continue typing
    if (displayedText.length < content.length) {
      setIsTyping(true);
      const timeout = setTimeout(() => {
        setDisplayedText(content.substring(0, displayedText.length + 1));
      }, typingSpeed);

      return () => clearTimeout(timeout);
    } else {
      setIsTyping(false);
    }
  }, [content, displayedText, isUser, isLatest]);

  // Auto-scroll as text is being typed
  useEffect(() => {
    if (!isUser && isTyping && isLatest && messageEndRef.current) {
      // Find the closest scrollable parent
      const scrollToBottom = () => {
        const scrollableParent = findScrollableParent(messageEndRef.current);
        if (scrollableParent) {
          scrollableParent.scrollTop = scrollableParent.scrollHeight;
        }
      };

      scrollToBottom();
    }
  }, [displayedText, isUser, isTyping, isLatest]);

  // Helper function to find the closest scrollable parent
  const findScrollableParent = (
    element: HTMLElement | null
  ): HTMLElement | null => {
    if (!element) return null;

    // Check if the element itself is scrollable
    if (
      element.scrollHeight > element.clientHeight ||
      getComputedStyle(element).overflowY === 'auto' ||
      getComputedStyle(element).overflowY === 'scroll'
    ) {
      return element;
    }

    // Check parent elements
    return findScrollableParent(element.parentElement);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard');
  };

  // Reset image error state when image prop changes
  useEffect(() => {
    setImgError(false);
  }, [image]);

  return (
    <div
      className={`flex flex-col w-full mb-4 ${isUser ? 'items-end' : 'items-start'}`}
    >
      {userName && (
        <div className="flex items-center mb-2">
          {image && !imgError ? (
            <img
              src={image}
              alt=""
              className="w-8 h-8 rounded-full bg-background-card mr-2"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className={`w-8 h-8 rounded-full mr-2 flex items-center justify-center ${isUser ? 'bg-primary text-white' : 'bg-background-card'}`}>
              {isUser ? (
                <span className="text-sm font-semibold">{userName.charAt(0).toUpperCase()}</span>
              ) : (
                <img 
                  src="/logo.png" 
                  alt=""
                  className="w-6 h-6 object-contain"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null; // Prevent infinite loop
                    target.style.display = 'none';
                  }}
                />
              )}
            </div>
          )}
          <span className="text-sm font-medium text-text-primary">
            {userName}
          </span>
        </div>
      )}

      <div className="relative w-full max-w-[85%] sm:max-w-[75%]">
        {!isUser && (
          <div className="absolute right-2 -top-8 flex gap-2">
            <button
              onClick={handleCopy}
              className="p-1  rounded-md transition-colors duration-200 flex items-center gap-1 text-sm text-primary"
            >
              <Copy size={16} />
            </button>
            {index == 0 ? (
              <></>
            ) : (
              <button
                onClick={() => handleRegenerate(index - 1)}
                className="p-1  rounded-md transition-colors duration-200 flex items-center gap-1 text-sm text-primary"
              >
                <Repeat size={16} />
              </button>
            )}
          </div>
        )}

        <div
          className={`rounded-lg p-4  ${isUser ? 'bg-[#D7D6FD] text-black ' : 'bg-[#202020] text-white'}`}
        >
          <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
            {isUser || !isLatest ? content : displayedText}
            {!isUser && isTyping && isLatest && (
              <span className="inline-block w-1 h-4 ml-0.5 bg-current animate-blink" />
            )}
          </p>
          <div ref={messageEndRef} />
        </div>
      </div>
    </div>
  );
};
